import { useState } from 'react';
import { Alert, App, Button, Input, Modal, Space, Typography } from 'antd';
import { apiErrorCode, apiErrorMessage } from '@/api/client';
import {
  APPLICATION_AI_MAX_CHARS,
  aiExtractApplication,
  type AiApplicationResult,
} from '@/api/payouts';
import AiApplicationEditor from './AiApplicationEditor';

const { Paragraph, Text, Title } = Typography;

export default function AiApplicationModal({ open, onClose, onApply }: {
  open: boolean;
  onClose: () => void;
  onApply: (result: AiApplicationResult) => void;
}) {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [parsedText, setParsedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AiApplicationResult | null>(null);
  const [manualFields, setManualFields] = useState<Set<string>>(() => new Set());
  const [notConfigured, setNotConfigured] = useState(false);
  const dirty = result !== null && text.trim() !== parsedText;

  function reset() {
    setText('');
    setParsedText('');
    setLoading(false);
    setResult(null);
    setManualFields(new Set());
    setNotConfigured(false);
  }

  async function parse() {
    const input = text.trim();
    if (!input) return;
    setLoading(true);
    setNotConfigured(false);
    try {
      const next = await aiExtractApplication(text);
      setResult(next);
      setManualFields(new Set());
      setParsedText(input);
    } catch (err) {
      if (apiErrorCode(err) === 'AI_NOT_CONFIGURED') setNotConfigured(true);
      else message.error(apiErrorMessage(err, 'AI 解析失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  }

  const ready = Boolean(
    result
      && result.values.amount?.trim()
      && result.values.reference.trim()
      && result.payee.fields
        .filter((field) => field.required)
        .every((field) => result.payee.values[field.path]?.trim()),
  );

  function markManual(key: string) {
    setManualFields((current) => new Set(current).add(key));
  }

  return (
    <Modal
      open={open}
      title="AI 录入打款申请"
      width={1440}
      onCancel={onClose}
      afterClose={reset}
      mask={{ closable: !loading }}
      footer={
        <Space>
          <Button onClick={onClose} disabled={loading}>取消</Button>
          <Button
            type="primary"
            disabled={!result || !ready || dirty || loading}
            onClick={() => result && onApply(result)}
          >
            确认并继续
          </Button>
        </Space>
      }
    >
      <AiApplicationEditor
        result={result}
        manualFields={manualFields}
        onResultChange={setResult}
        onManualField={markManual}
        original={(
          <>
            <Title level={5} style={{ marginTop: 0 }}>1. 输入申请信息</Title>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              可以输入聊天记录、邮件或一句自然语言。AI 会同时解析一个全新的 SWIFT 收款人，以及
              金额、银行附言和内部备注；不会匹配或复用已有收款人。
            </Paragraph>
            <Input.TextArea
              value={text}
              onChange={(event) => setText(event.target.value)}
              disabled={loading}
              autoSize={{ minRows: 15, maxRows: 22 }}
              maxLength={APPLICATION_AI_MAX_CHARS}
              placeholder={'例如：\n给 Alice Chen 打 1,200.50 USD\nSWIFT: CHASUS33\nAccount No.: 50001121\nAddress: 412 5th Avenue, Seattle, WA 98104\n银行附言 KOL Marketing'}
            />
            <div
              data-testid="ai-application-input-count"
              style={{ display: 'flex', justifyContent: 'flex-end', minHeight: 20, paddingTop: 4 }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {text.length} / {APPLICATION_AI_MAX_CHARS}
              </Text>
            </div>
            <Button
              type="primary"
              block
              style={{ marginTop: 4 }}
              loading={loading}
              disabled={!text.trim() || notConfigured}
              onClick={() => void parse()}
            >
              {result ? '重新解析' : '解析申请'}
            </Button>
            {dirty && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                title="左侧内容已修改"
                description="请重新解析后再确认，避免右侧仍是旧结果。"
              />
            )}
            {notConfigured ? (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                title="本环境未启用 AI 录入"
                description="你仍然可以关闭弹窗后，手动创建收款人和申请。"
              />
            ) : (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                title="将创建新 SWIFT 收款人，不会打款"
                description="你输入的内容会发送给第三方 AI 服务解析，邮编等公开地址字段可能使用联网搜索。确认后仍需在下一步提交；提交只会原子创建新收款人与申请，执行打款仍是后续独立操作。"
              />
            )}
          </>
        )}
      />
    </Modal>
  );
}
